import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Row,
  Col,
  Divider,
  Card,
  message,
} from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { BiSearch } from "react-icons/bi";
import { serverUrl } from "../../../../../nestserver";
import { DeleteOutlined, UnorderedListOutlined } from "@ant-design/icons";
import plusicon from "../../../../../resources/plus.svg";

const AdminMontessoriTemplateCard = () => {
  const { Search } = Input;
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const classIdFromUrl = queryParams.get("classId") || null;

  const [addTemplate, setAddTemplate] = useState(false);
  const [classId, setClassId] = useState(null);
  const [domains, setDomains] = useState([]);
  const [newDomainName, setNewDomainName] = useState("");
  const [newMilestones, setNewMilestones] = useState({});
  const [active_terms, setActiveTerms] = useState([]);
  const [newTerm, setNewTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    queryParams.get("search") || "",
  );
  const [getClassId, setGetClassId] = useState(classIdFromUrl);

  // fetch all classes (admin can see all)
  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);

  // fetch templates
  const fetchTemplates = async (getClassId) => {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/montessori-progress-card-template/?class=${getClassId}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const templates = useQuery(
    ["montessori-admin-templates", getClassId],
    () => fetchTemplates(getClassId),
    {
      enabled: !!getClassId,
    },
  );

  const handleAddDomain = () => {
    if (!newDomainName.trim()) {
      message.warning("Domain name cannot be empty");
      return;
    }
    const newDomain = {
      id: Date.now(),
      name: newDomainName.trim(),
      milestones: [],
    };
    setDomains((prev) => [...prev, newDomain]);
    setNewDomainName("");
  };

  const handleAddMilestone = (domainId) => {
    const milestoneText = newMilestones[domainId]?.trim();
    if (!milestoneText) {
      message.warning("Milestone description cannot be empty");
      return;
    }
    setDomains((prev) =>
      prev.map((d) =>
        d.id === domainId
          ? {
              ...d,
              milestones: [
                ...d.milestones,
                { id: Date.now(), description: milestoneText },
              ],
            }
          : d,
      ),
    );
    setNewMilestones((prev) => ({ ...prev, [domainId]: "" }));
  };

  const handleRemoveDomain = (domainId) => {
    setDomains((prev) => prev.filter((d) => d.id !== domainId));
  };

  const handleRemoveMilestone = (domainId, milestoneId) => {
    setDomains((prev) =>
      prev.map((d) =>
        d.id === domainId
          ? {
              ...d,
              milestones: d.milestones.filter((m) => m.id !== milestoneId),
            }
          : d,
      ),
    );
  };

  const handleAddTerm = () => {
    if (!newTerm.trim()) {
      message.warning("Term name cannot be empty");
      return;
    }
    if (active_terms.includes(newTerm.trim())) {
      message.warning("Term already exists");
      return;
    }
    setActiveTerms((prev) => [...prev, newTerm.trim()]);
    setNewTerm("");
  };

  const handleRemoveTerm = (termToRemove) => {
    setActiveTerms((prev) => prev.filter((t) => t !== termToRemove));
  };

  const handleDeleteTemplate = (id) => {
    axios
      .delete(serverUrl + "/montessori-progress-card-template/" + id, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        queryClient.refetchQueries(["montessori-admin-templates", getClassId]);
        message.success("Template deleted successfully");
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          message.error(err?.response?.data?.message);
        }
      });
  };

  const onFinish = (values) => {
    if (domains.length === 0) {
      message.warning("Please add at least one domain");
      return;
    }
    if (active_terms.length === 0) {
      message.warning("Please add at least one term (e.g. Term 1)");
      return;
    }
    const academicYear = values.year
      ? `${values.year[0].format("YYYY")}-${values.year[1].format("YYYY")}`
      : "";
    const payload = {
      name: values.title,
      academic_year: academicYear,
      class: classId,
      active_terms: active_terms,
      domains: domains.map((d) => ({
        name: d.name,
        milestones: d.milestones.map((m) => ({ description: m.description })),
      })),
    };
    axios
      .post(serverUrl + "/montessori-progress-card-template/", payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        queryClient.refetchQueries(["montessori-admin-templates", getClassId]);
        message.success("Template created successfully");
        setAddTemplate(false);
        form.resetFields();
        setDomains([]);
        setActiveTerms([]);
        setClassId(null);
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          message.error(err?.response?.data?.message);
        }
      });
  };

  const filteredTemplates =
    templates?.isSuccess &&
    templates?.data?.result?.length > 0 &&
    templates?.data?.result?.filter(
      (t) =>
        t?.name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        t?.class?._id === getClassId,
    );

  const handleGetClass = (id) => {
    setGetClassId(id);
    queryParams.set("classId", id);
    navigate({ search: queryParams.toString() });
  };

  return (
    <>
      <div className="template">
        <div className="pt-8">
          <div className="bg-white p-10 rounded-2xl">
            <div className="text-2xl text-orange-500 font-semibold pb-8">
              Progress card Template
            </div>
            <div className="flex justify-between pb-4">
              <div>
                <Select
                  showSearch
                  style={{ width: 200 }}
                  placeholder="Select a class"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  onChange={handleGetClass}
                  value={getClassId}
                >
                  {classes?.isSuccess &&
                    classes?.data?.map((c) => (
                      <Select.Option key={c?._id} value={c?._id}>
                        {c?.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
              <Search
                placeholder="input search text"
                enterButton="Search"
                size="large"
                loading={false}
                style={{ backgroundColor: "#E3E3E3" }}
                prefix={<BiSearch className="text-gray-600 " />}
                onSearch={(v) => {
                  setSearchQuery(v);
                  if (v) {
                    queryParams.set("search", v);
                  } else {
                    queryParams.delete("search");
                  }
                  navigate({ search: queryParams.toString() });
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchQuery(v);
                  if (v) {
                    queryParams.set("search", v);
                  } else {
                    queryParams.delete("search");
                  }
                  navigate({ search: queryParams.toString() });
                }}
                className="w-310"
                value={searchQuery}
              />
            </div>

            <div className="flex justify-between items-start pt-4">
              <div className="flex gap-20px flex-wrap">
                {filteredTemplates && filteredTemplates ? (
                  filteredTemplates.map((template) => (
                    <div key={template._id}>
                      <div
                        className="iskool-friends-card flex flex-col space-x-3 cursor-pointer h-full p-20px"
                        style={{ width: "190px" }}
                      >
                        <div
                          className="text-center mt-14px"
                          onClick={() =>
                            navigate(
                              `/dashboard/admin/menu/montessori-report/template/${template._id}`,
                            )
                          }
                        >
                          <UnorderedListOutlined style={{ fontSize: 35 }} />
                          <div className="pt-2">
                            <div className="font-bold capitalize">
                              {template.class?.name}
                            </div>
                            {/* <div className="font-bold capitalize line-clamp-1">
                              {template.name}
                            </div> */}
                            <div className="text-xs text-gray-400">
                              {template.academic_year}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-center pt-2">
                          <Popconfirm
                            title="Are you sure to delete?"
                            onConfirm={() => handleDeleteTemplate(template._id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="primary"
                              danger
                              className="flex items-center justify-center"
                              shape="circle"
                              icon={
                                <DeleteOutlined
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                />
                              }
                            />
                          </Popconfirm>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div>No Template Exist</div>
                )}
              </div>
              <div
                onClick={() => setAddTemplate(true)}
                className="cursor-pointer p-28px add-module flex items-center justify-center mt-2"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="flex items-center justify-center cursor-pointer">
                    <img className="w-6 h-6" src={plusicon} alt="icon" />
                  </div>
                  <div className="text-white text-sm">
                    <div>Add New</div>
                    <div>Template</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add Template Modal */}
      <Modal
        title="Create Template"
        visible={addTemplate}
        onCancel={() => {
          setAddTemplate(false);
          form.resetFields();
          setDomains([]);
          setActiveTerms([]);
          setClassId(null);
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Template Name"
                name="title"
                rules={[
                  { required: true, message: "Please enter template name" },
                ]}
              >
                <Input placeholder="Enter Template Name" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Academic Year"
                name="year"
                rules={[
                  { required: true, message: "Please select academic year" },
                ]}
              >
                <DatePicker.RangePicker
                  picker="year"
                  className="w-full"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Class"
                name="class"
                rules={[{ required: true, message: "Please select a class" }]}
              >
                <Select
                  showSearch
                  placeholder="Select class"
                  optionFilterProp="children"
                  size="large"
                  onChange={(value) => setClassId(value)}
                >
                  {classes?.data?.map((c) => (
                    <Select.Option key={c._id} value={c._id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ marginTop: 0 }}>
            Configuration
          </Divider>

          {/* Terms Section */}
          <Card
            size="small"
            className="mb-6 shadow-sm"
            style={{ borderRadius: "8px" }}
          >
            <Form.Item
              label="Add Term (e.g. Term 1, Term 2)"
              extra="Press Enter or click Add to include a term"
            >
              <div className="flex gap-4">
                <Input
                  placeholder="Term name (e.g. Term 1)"
                  value={newTerm}
                  size="large"
                  onChange={(e) => setNewTerm(e.target.value)}
                  onPressEnter={handleAddTerm}
                />
                <Button type="primary" size="large" onClick={handleAddTerm}>
                  Add
                </Button>
              </div>
            </Form.Item>

            {active_terms.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-6 p-5 bg-orange-50/30 border border-dashed border-orange-200 rounded-xl">
                {active_terms.map((term, index) => (
                  <div
                    key={index}
                    className="bg-white border border-orange-100 text-orange-700 px-4 py-2 rounded-lg flex items-center gap-3 shadow-sm hover:shadow-md hover:border-orange-300 transition-all duration-200"
                  >
                    <span className="text-sm font-semibold tracking-wide">
                      {term}
                    </span>
                    <Divider
                      type="vertical"
                      className="m-0 border-orange-100"
                    />
                    <DeleteOutlined
                      className="cursor-pointer text-xs text-red-400 hover:text-red-600 hover:scale-110 transition-transform"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => handleRemoveTerm(term)}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Domains Section */}
          <Card
            title="Categories & Skills"
            size="small"
            className="mb-6 shadow-sm"
            style={{ borderRadius: "8px" }}
          >
            <Form.Item
              label="Add Category"
              required
              tooltip="Enter a category name and click Add"
              extra={`${domains.length}/10 Categories added`}
            >
              <div className="flex gap-4">
                <Input
                  placeholder="Category name (e.g. Practical Life)"
                  value={newDomainName}
                  size="large"
                  onChange={(e) => setNewDomainName(e.target.value)}
                  onPressEnter={
                    domains.length < 10 ? handleAddDomain : undefined
                  }
                  disabled={domains.length >= 10}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={handleAddDomain}
                  disabled={domains.length >= 10}
                >
                  Add
                </Button>
              </div>
              {domains.length >= 10 && (
                <div className="text-red-500 text-xs mt-1">
                  Maximum limit of 10 categories reached.
                </div>
              )}
            </Form.Item>

            <div className="space-y-4 mt-6">
              {domains.map((domain) => (
                <div
                  key={domain.id}
                  className="border border-gray-200 rounded-xl overflow-hidden shadow-sm"
                >
                  <div className="bg-gray-50 px-4 py-2 flex justify-between items-center border-b border-gray-200">
                    <div className="font-bold text-gray-700 capitalize">
                      {domain.name}
                    </div>
                    <DeleteOutlined
                      className="text-red-500 cursor-pointer hover:scale-110 transition-transform"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => handleRemoveDomain(domain.id)}
                    />
                  </div>
                  <div className="p-4 bg-white">
                    <div className="space-y-2 mb-4">
                      {domain.milestones.map((m) => (
                        <div
                          key={m.id}
                          className="flex justify-between items-center px-3 py-2 bg-blue-50/50 rounded-lg group"
                        >
                          <span className="text-sm text-gray-700">
                            {m.description}
                          </span>
                          <DeleteOutlined
                            className="text-red-400 cursor-pointer text-xs"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() =>
                              handleRemoveMilestone(domain.id, m.id)
                            }
                          />
                        </div>
                      ))}
                    </div>

                    <Form.Item
                      label={`Add Milestone (${domain.milestones.length}/10)`}
                      className="mb-0"
                    >
                      <div className="flex gap-4">
                        <Input
                          placeholder="Add milestone description..."
                          value={newMilestones[domain.id] || ""}
                          onChange={(e) =>
                            setNewMilestones((prev) => ({
                              ...prev,
                              [domain.id]: e.target.value,
                            }))
                          }
                          onPressEnter={() =>
                            domain.milestones.length < 10
                              ? handleAddMilestone(domain.id)
                              : undefined
                          }
                          disabled={domain.milestones.length >= 10}
                        />
                        <Button
                          type="primary"
                          onClick={() => handleAddMilestone(domain.id)}
                          disabled={domain.milestones.length >= 10}
                        >
                          Add
                        </Button>
                      </div>
                    </Form.Item>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              onClick={() => {
                setAddTemplate(false);
                form.resetFields();
                setDomains([]);
                setActiveTerms([]);
                setClassId(null);
              }}
              size="large"
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" size="large">
              Create Template
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default AdminMontessoriTemplateCard;
